import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Tag,
  Share2,
  Facebook,
  Linkedin,
  Link2,
  Check,
  List,
  Calendar,
  ArrowRight,
  HelpCircle,
} from 'lucide-react';
import AltHeader from '../../components/AltHeader';
import AltFooter from '../../components/AltFooter';
import AltParallaxBackdrop from '../../components/AltParallaxBackdrop';
import blogArticles from '../../data/blogArticles';
import { getArticleImage, getResponsiveSrc } from '../../data/categoryImages';
import { buildToc, wordCount } from '../../data/blogToc';
import { enrichArticleHtml } from '../../data/enrichArticle';

/* Brand SVG icons (lucide doesn't ship X / WhatsApp / Reddit logos). */
const XIcon = ({ className = 'w-4 h-4' }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);
const WhatsAppIcon = ({ className = 'w-4 h-4' }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M20.52 3.48A11.86 11.86 0 0 0 12.04 0C5.5 0 .2 5.3.2 11.84c0 2.09.55 4.12 1.6 5.91L0 24l6.4-1.68a11.83 11.83 0 0 0 5.64 1.43h.01c6.55 0 11.84-5.3 11.84-11.84a11.79 11.79 0 0 0-3.37-8.43zM12.05 21.7h-.01a9.86 9.86 0 0 1-5.02-1.37l-.36-.21-3.8 1 1.01-3.7-.23-.38a9.85 9.85 0 0 1-1.51-5.2c0-5.44 4.43-9.87 9.87-9.87a9.81 9.81 0 0 1 6.98 2.89 9.81 9.81 0 0 1 2.89 6.98c0 5.44-4.43 9.86-9.82 9.86zm5.4-7.39c-.3-.15-1.75-.86-2.02-.96-.27-.1-.47-.15-.67.15-.2.3-.77.96-.94 1.16-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.62-.92-2.21-.24-.58-.49-.5-.67-.51l-.57-.01a1.1 1.1 0 0 0-.8.37c-.27.3-1.04 1.02-1.04 2.49 0 1.47 1.07 2.89 1.22 3.09.15.2 2.11 3.22 5.11 4.51.71.31 1.27.49 1.71.63.72.23 1.37.2 1.89.12.58-.09 1.75-.71 2-1.4.25-.69.25-1.28.17-1.4-.07-.12-.27-.2-.57-.35z" />
  </svg>
);
const RedditIcon = ({ className = 'w-4 h-4' }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M22 12.07c0-1.21-.98-2.19-2.19-2.19-.59 0-1.13.24-1.52.62-1.5-1.03-3.55-1.69-5.83-1.77l1-4.7 3.27.69c.04.83.72 1.49 1.55 1.49.86 0 1.55-.7 1.55-1.55s-.69-1.55-1.55-1.55c-.61 0-1.13.35-1.39.86l-3.65-.77a.39.39 0 0 0-.46.3l-1.11 5.22c-2.32.07-4.39.74-5.91 1.78a2.18 2.18 0 0 0-1.52-.62A2.19 2.19 0 0 0 2 12.07c0 .87.51 1.62 1.25 1.96-.04.21-.06.43-.06.65 0 3.31 3.85 6 8.6 6s8.6-2.69 8.6-6c0-.22-.02-.44-.06-.65A2.18 2.18 0 0 0 22 12.07zM7 13.43c0-.86.69-1.55 1.55-1.55s1.55.69 1.55 1.55-.69 1.55-1.55 1.55S7 14.29 7 13.43zm8.79 4.06c-1.07 1.07-3.13 1.16-3.74 1.16s-2.67-.08-3.74-1.16a.41.41 0 0 1 .58-.58c.68.68 2.13.92 3.16.92s2.48-.24 3.16-.92a.41.41 0 0 1 .58.58zm-.34-2.51c-.86 0-1.55-.69-1.55-1.55s.69-1.55 1.55-1.55 1.55.69 1.55 1.55-.69 1.55-1.55 1.55z" />
  </svg>
);

const BlogPost = () => {
  const { slug } = useParams();
  const article = blogArticles.find((a) => a.slug === slug);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (article) {
      /* Match the backend's _seo_blog_title helper exactly so the
         server-injected <title> and the runtime title stay identical. */
      const MAX = 70;
      const suffix = ' | Levanta';
      const t = article.title;
      let final;
      if (t.length + suffix.length <= MAX) {
        final = `${t}${suffix}`;
      } else if (t.length <= MAX) {
        final = t;
      } else {
        const cut = t
          .slice(0, MAX - 1)
          .replace(/\s+\S*$/, '')
          .replace(/[,:;-]+$/, '');
        final = `${cut}\u2026`;
      }
      document.title = final;
      window.scrollTo(0, 0);
    }
  }, [article]);

  /* AEO: inject FAQPage JSON-LD into <head> when this article ships an
     `faq` array. Cleaned up on unmount so it never leaks into another
     route. Answer engines (ChatGPT, Perplexity, Gemini, Google AI
     Overviews) cite pages that expose Q→A structure cleanly. */
  useEffect(() => {
    if (!article || !Array.isArray(article.faq) || article.faq.length === 0) {
      return undefined;
    }
    const id = `lv-faq-jsonld-${article.slug}`;
    const existing = document.getElementById(id);
    if (existing) existing.remove();
    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = id;
    el.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: article.faq.map(({ q, a }) => ({
        '@type': 'Question',
        name: q,
        acceptedAnswer: { '@type': 'Answer', text: a },
      })),
    });
    document.head.appendChild(el);
    return () => {
      const node = document.getElementById(id);
      if (node) node.remove();
    };
  }, [article]);

  /* SEO: inject BlogPosting JSON-LD per article. Gives Google's "Article"
     rich result eligibility (date, author, hero image) and gives LLM
     crawlers a clean entity for citation. */
  useEffect(() => {
    if (!article) return undefined;
    const id = `lv-article-jsonld-${article.slug}`;
    const existing = document.getElementById(id);
    if (existing) existing.remove();
    const url = `https://levanta.life/blog/${article.slug}`;
    const heroImage =
      article.image ||
      'https://levanta.life/press/levanta-feature-graphic.png';
    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = id;
    el.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      mainEntityOfPage: { '@type': 'WebPage', '@id': url },
      headline: article.title,
      description: article.description,
      image: [heroImage],
      datePublished: article.publishedAt,
      dateModified: article.modifiedAt || article.publishedAt,
      author: {
        '@type': 'Organization',
        name: 'Levanta',
        url: 'https://levanta.life',
      },
      publisher: {
        '@type': 'Organization',
        name: 'Levanta',
        logo: {
          '@type': 'ImageObject',
          url: 'https://levanta.life/favicon.png',
        },
      },
      keywords: article.keywords,
      articleSection: article.category,
      url,
    });
    document.head.appendChild(el);
    return () => {
      const node = document.getElementById(id);
      if (node) node.remove();
    };
  }, [article]);

  // Hooks must be called on every render — handles the not-found case with
  // an empty string so the order stays stable.
  const enrichedContent = useMemo(
    () => (article ? enrichArticleHtml(article.content || '', article) : ''),
    [article]
  );
  const { contentWithIds, toc } = useMemo(
    () => buildToc(enrichedContent),
    [enrichedContent]
  );

  if (!article) {
    return (
      <div className="bg-white overflow-x-hidden">
        <AltHeader />
        <section className="relative pt-[160px] md:pt-[200px] pb-24 md:pb-32 px-6 overflow-hidden">
          <AltParallaxBackdrop />
          <div className="relative z-10 max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-7xl font-black leading-[1.02] tracking-tight text-[#0b1f3a]">
              Article{' '}
              <em className="italic font-light text-blue-600">not found.</em>
            </h1>
            <p className="mt-7 text-lg text-[#0b1f3a]/70">
              The link you followed may be broken, or the article may have moved.
            </p>
            <Link
              to="/blog"
              data-testid="blog-back-link"
              className="mt-10 inline-flex items-center gap-3 px-8 py-4 rounded-full bg-[#0b1f3a] text-white font-semibold shadow-md hover:shadow-2xl hover:-translate-y-0.5 transition-all duration-200"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to the blog
            </Link>
          </div>
        </section>
        <AltFooter />
      </div>
    );
  }

  const relatedArticles = blogArticles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const heroImage = getArticleImage(article);
  const shareUrl = `https://levanta.life/blog/${article.slug}`;
  const shareText = article.title;
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedText = encodeURIComponent(shareText);

  const showToc = toc.length >= 3 || wordCount(article.content || '') >= 800;

  const publishedDisplay = article.publishedAt
    ? new Date(article.publishedAt).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : null;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers.
      const textarea = document.createElement('textarea');
      textarea.value = shareUrl;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareButtons = [
    {
      key: 'x',
      label: 'X',
      href: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
      icon: <XIcon className="w-4 h-4" />,
      className: 'bg-black hover:bg-neutral-800 text-white',
    },
    {
      key: 'facebook',
      label: 'Facebook',
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      icon: <Facebook className="w-4 h-4" />,
      className: 'bg-[#1877F2] hover:bg-[#166FE5] text-white',
    },
    {
      key: 'linkedin',
      label: 'LinkedIn',
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      icon: <Linkedin className="w-4 h-4" />,
      className: 'bg-[#0A66C2] hover:bg-[#095198] text-white',
    },
    {
      key: 'whatsapp',
      label: 'WhatsApp',
      href: `https://api.whatsapp.com/send?text=${encodedText}%20${encodedUrl}`,
      icon: <WhatsAppIcon className="w-4 h-4" />,
      className: 'bg-[#25D366] hover:bg-[#1FBA59] text-white',
    },
    {
      key: 'reddit',
      label: 'Reddit',
      href: `https://reddit.com/submit?url=${encodedUrl}&title=${encodedText}`,
      icon: <RedditIcon className="w-4 h-4" />,
      className: 'bg-[#FF4500] hover:bg-[#E03D00] text-white',
    },
  ];

  return (
    <div className="bg-white overflow-x-hidden">
      <AltHeader />

      {/* HERO — gradient parallax forest on desktop, clean white on mobile
          per design feedback (the traffic-of-moving-trees backdrop felt
          noisy on small screens where the header already sits tight
          against the text). Section min-height also collapses on mobile
          so there is no forced empty area below the title. */}
      <section className="relative pt-[110px] md:pt-[170px] pb-12 md:pb-44 px-6 overflow-hidden md:min-h-[760px] bg-white md:bg-transparent">
        <div className="hidden md:block"><AltParallaxBackdrop /></div>

        <div className="relative z-10 max-w-4xl mx-auto">
          <Link
            to="/blog"
            data-testid="blog-back-link"
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#0b1f3a]/70 hover:text-[#0b1f3a] transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to the blog
          </Link>

          <div className="flex items-center gap-3 mb-6 flex-wrap">
            <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-white px-3 py-1.5 rounded-full bg-[#0b1f3a]">
              <Tag className="w-3 h-3" />
              {article.category}
            </span>
            {publishedDisplay && (
              <span
                className="inline-flex items-center gap-1.5 text-sm text-[#0b1f3a]/60"
                data-testid="blog-post-published-date"
              >
                <Calendar className="w-3.5 h-3.5" />
                <time dateTime={article.publishedAt}>{publishedDisplay}</time>
              </span>
            )}
          </div>

          <h1
            className="text-4xl md:text-6xl font-black leading-[1.05] tracking-tight text-[#0b1f3a] alt-hero-glow"
          >
            {article.title}
          </h1>
          {article.description && (
            <p className="mt-6 text-lg md:text-xl text-[#0b1f3a]/75 leading-relaxed max-w-3xl alt-hero-glow">
              {article.description}
            </p>
          )}
        </div>
      </section>

      {/* HERO IMAGE — overlaps the hero on desktop (cinematic effect).
          On mobile the hero is now white, so we use a much smaller
          negative margin just to separate the image from the title
          nicely without any overlap that would look weird on the flat
          white background. */}
      {heroImage && (
        <div className="px-6 -mt-2 md:-mt-32 relative z-10">
          <div className="max-w-4xl mx-auto">
            <div className="rounded-3xl overflow-hidden shadow-2xl ring-1 ring-black/5 aspect-[16/9] bg-[#fafbfd]">
              {(() => {
                const r = getResponsiveSrc(heroImage, {
                  sizes: '(max-width: 768px) 92vw, 56rem',
                });
                return (
                  <img
                    src={r?.src}
                    srcSet={r?.srcSet}
                    sizes={r?.sizes}
                    alt={article.title}
                    data-testid="blog-post-hero-image"
                    className="w-full h-full object-cover"
                    width="1200"
                    height="675"
                    loading="eager"
                    fetchPriority="high"
                  />
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* BODY */}
      <article className="px-6 pt-16 md:pt-20 pb-16 md:pb-20 bg-white">
        <div className="max-w-3xl mx-auto">
          {/* Table of Contents */}
          {showToc && (
            <nav
              aria-label="Table of contents"
              data-testid="blog-toc"
              className="mb-12 rounded-2xl bg-[#fafbfd] ring-1 ring-black/5 p-6 md:p-7"
            >
              <h2 className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-600 mb-4">
                <List className="w-3.5 h-3.5" />
                In this article
              </h2>
              <ol className="space-y-2.5">
                {toc.map((item, idx) => (
                  <li key={item.id} className="flex gap-3 text-[#0b1f3a]/80">
                    <span className="text-blue-500 font-mono text-xs font-semibold w-6 flex-shrink-0 pt-0.5">
                      {String(idx + 1).padStart(2, '0')}
                    </span>
                    <a
                      href={`#${item.id}`}
                      className="hover:text-blue-600 hover:underline decoration-blue-600/30 underline-offset-4 transition-colors"
                    >
                      {item.text}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>
          )}

          {/* Article HTML */}
          <div
            className="prose prose-lg max-w-none
                       prose-headings:text-[#0b1f3a] prose-headings:font-black prose-headings:tracking-tight
                       prose-h2:scroll-mt-24 prose-h2:text-3xl md:prose-h2:text-4xl prose-h2:mt-12 prose-h2:mb-5
                       prose-h3:text-xl md:prose-h3:text-2xl prose-h3:mt-8 prose-h3:mb-3
                       prose-p:text-[#0b1f3a]/80 prose-p:leading-relaxed
                       prose-strong:text-[#0b1f3a]
                       prose-a:text-blue-600 prose-a:font-semibold prose-a:no-underline hover:prose-a:underline prose-a:decoration-blue-600/30 prose-a:underline-offset-4
                       prose-li:text-[#0b1f3a]/80 prose-li:leading-relaxed
                       prose-img:rounded-2xl prose-img:shadow-sm
                       prose-blockquote:border-l-blue-500 prose-blockquote:bg-[#f4f8fc] prose-blockquote:py-4 prose-blockquote:px-6 prose-blockquote:rounded-r-2xl prose-blockquote:not-italic prose-blockquote:text-[#0b1f3a]
                       prose-code:bg-[#fafbfd] prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded-md prose-code:text-[#0b1f3a] prose-code:before:hidden prose-code:after:hidden"
            dangerouslySetInnerHTML={{ __html: contentWithIds }}
          />

          {/* FAQ — visible counterpart to the FAQPage JSON-LD. Renders
              only when the article ships an `faq` array. Each Q→A is
              given an id so the JSON-LD anchors line up with the
              visible answer for crawlers. */}
          {Array.isArray(article.faq) && article.faq.length > 0 && (
            <section
              className="mt-16 pt-10 border-t border-black/5"
              data-testid="blog-post-faq"
              aria-label="Frequently asked questions"
            >
              <h2
                id="faq"
                className="flex items-center gap-3 text-3xl md:text-4xl font-black tracking-tight text-[#0b1f3a] mb-8 scroll-mt-24"
              >
                <HelpCircle className="w-7 h-7 text-blue-600" />
                Frequently asked questions
              </h2>
              <div className="space-y-4">
                {article.faq.map((item, idx) => (
                  <details
                    key={idx}
                    className="group rounded-2xl bg-[#fafbfd] ring-1 ring-black/5 px-6 py-5 open:bg-white open:ring-blue-200 open:shadow-sm transition-colors"
                    data-testid={`blog-faq-item-${idx}`}
                  >
                    <summary className="flex items-start justify-between gap-4 cursor-pointer list-none">
                      <h3 className="text-base md:text-lg font-bold text-[#0b1f3a] leading-snug">
                        {item.q}
                      </h3>
                      <span
                        aria-hidden="true"
                        className="mt-1 shrink-0 w-7 h-7 rounded-full bg-white ring-1 ring-black/5 text-[#0b1f3a]/60 flex items-center justify-center text-xl leading-none transition-transform duration-200 group-open:rotate-45"
                      >
                        +
                      </span>
                    </summary>
                    <p className="mt-4 text-[#0b1f3a]/75 leading-relaxed text-[15px] md:text-base">
                      {item.a}
                    </p>
                  </details>
                ))}
              </div>
            </section>
          )}

          {/* Tags */}
          {article.keywords && (
            <div className="mt-12 pt-8 border-t border-black/5">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#0b1f3a]/55 mb-3">
                Tagged
              </p>
              <div className="flex flex-wrap gap-2">
                {article.keywords.split(', ').map((keyword, i) => (
                  <span
                    key={i}
                    className="px-3 py-1 rounded-full bg-[#fafbfd] ring-1 ring-black/5 text-[#0b1f3a]/70 text-sm hover:text-blue-600 hover:ring-blue-200 transition-colors"
                  >
                    #{keyword.replace(/\s+/g, '')}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Share */}
          <div className="mt-12 pt-8 border-t border-black/5">
            <div className="flex items-center gap-2 text-[#0b1f3a] mb-4">
              <Share2 className="w-4 h-4" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.22em]">
                Share this article
              </span>
            </div>
            <div className="flex flex-wrap gap-2" data-testid="blog-share-buttons">
              {shareButtons.map((btn) => (
                <a
                  key={btn.key}
                  href={btn.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`blog-share-${btn.key}`}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold shadow-sm hover:-translate-y-0.5 hover:shadow-md transition-all duration-200 ${btn.className}`}
                  aria-label={`Share on ${btn.label}`}
                >
                  {btn.icon}
                  <span>{btn.label}</span>
                </a>
              ))}
              <button
                type="button"
                onClick={handleCopyLink}
                data-testid="blog-share-copy"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold bg-[#fafbfd] ring-1 ring-black/5 text-[#0b1f3a] hover:bg-white hover:ring-blue-200 hover:-translate-y-0.5 transition-all duration-200"
                aria-label="Copy article link"
              >
                {copied ? (
                  <Check className="w-4 h-4 text-emerald-600" />
                ) : (
                  <Link2 className="w-4 h-4" />
                )}
                <span>{copied ? 'Copied!' : 'Copy link'}</span>
              </button>
            </div>
          </div>
        </div>
      </article>

      {/* CTA — dark navy, matches site language. */}
      <section className="px-6 py-20 md:py-24 bg-[#0b1f3a]">
        <div className="max-w-3xl mx-auto text-center text-white">
          <h2 className="text-4xl md:text-5xl font-black leading-tight">
            Ready to{' '}
            <em className="italic font-light text-blue-300">do the work?</em>
          </h2>
          <p className="mt-6 text-lg md:text-xl text-white/75">
            Reading is step one. Levanta turns it into a daily structure you actually
            return to.
          </p>
          <div className="mt-10">
            <Link
              to="/download-app"
              data-testid="blog-cta-download-btn"
              className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-white text-[#0b1f3a] font-semibold shadow-md hover:shadow-2xl hover:-translate-y-0.5 transition-all duration-200"
            >
              Download Levanta
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Related */}
      {relatedArticles.length > 0 && (
        <section className="px-6 py-20 md:py-24 bg-white">
          <div className="max-w-6xl mx-auto">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-600 mb-3 text-center">
              Keep reading
            </p>
            <h3 className="text-3xl md:text-4xl font-black leading-tight tracking-tight text-[#0b1f3a] text-center mb-12">
              More from{' '}
              <em className="italic font-light text-blue-600">{article.category}.</em>
            </h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {relatedArticles.map((related) => (
                <Link
                  key={related.id}
                  to={`/blog/${related.slug}`}
                  data-testid={`related-article-${related.slug}`}
                  className="group flex flex-col rounded-3xl bg-white ring-1 ring-black/5 overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl hover:ring-blue-200"
                >
                  <div className="aspect-[16/10] overflow-hidden bg-[#fafbfd]">
                    {(() => {
                      const r = getResponsiveSrc(getArticleImage(related));
                      return (
                        <img
                          src={r?.src}
                          srcSet={r?.srcSet}
                          sizes={r?.sizes}
                          alt={related.title}
                          loading="lazy"
                          width="1200"
                          height="675"
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      );
                    })()}
                  </div>
                  <div className="p-6 md:p-7 flex flex-col flex-1">
                    <span className="inline-flex self-start items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-blue-600 mb-3">
                      <Tag className="w-3 h-3" />
                      {related.category}
                    </span>
                    <h4 className="text-lg md:text-xl font-black text-[#0b1f3a] leading-tight mb-3 group-hover:text-blue-600 transition-colors duration-200">
                      {related.title}
                    </h4>
                    <p className="text-[#0b1f3a]/65 text-[15px] leading-relaxed line-clamp-2 mb-5">
                      {related.description}
                    </p>
                    <span className="mt-auto inline-flex items-center gap-2 text-blue-600 font-semibold text-sm group-hover:gap-3 transition-all duration-200">
                      Read more
                      <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <AltFooter />
    </div>
  );
};

export default BlogPost;
